import type { FileRejection } from "@mantine/dropzone"
import { AxiosError } from "axios"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  findIngestionsQueryKey,
  useConfirmEcoModulationImport,
  useFindIngestionErrorLogs,
  useFindIngestions,
  useValidateEcoModulationImport,
  type EcoModulationImportValidationErrorDto,
  type FindIngestionErrorLogsResponseDto,
  type FindIngestionsFiltersDto,
  type ValidateEcoModulationImportResponseDto,
  findIngestionsFiltersDtoTypeEnum,
  findIngestionsResponseDtoStatusEnum,
} from "~/gen/api"
import type { IngestionSortsType } from "~/pages/Brand/DataTransfer/Import/components/IngestionsSorts"
import {
  FILE_FORMAT_REJECT_ERROR,
  INGESTION_POLL_MS,
  VALIDATION_MIN_VISIBLE_MS,
} from "~/pages/Brand/DataTransfer/Import/const/ecoModulationImport.const"
import { useAuth } from "~/store/auth-context"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export type ConfirmPhase = "idle" | "running" | "failed"

type UseEcoModulationImportFlowParams = {
  onClose: () => void
  sorts: IngestionSortsType
  filters: FindIngestionsFiltersDto
}

const LINE_NUMBERS_PREFIX = /^Lines\s*:\s*/i

const mapIngestionErrorLogsToRowErrors = (
  errorLogs: FindIngestionErrorLogsResponseDto | null | undefined
): EcoModulationImportValidationErrorDto[] => {
  if (errorLogs === null || errorLogs === undefined) {
    return []
  }

  return Object.entries(errorLogs.content).flatMap(([message, lines]) => {
    const rowNumbers = lines
      .replace(LINE_NUMBERS_PREFIX, "")
      .split(",")
      .map((part) => Number(part.trim()))
      .filter((rowNumber) => Number.isFinite(rowNumber) && rowNumber > 0)

    if (rowNumbers.length === 0) {
      return [{ rowNumber: 0, message }]
    }

    return rowNumbers.map((rowNumber) => {
      return { rowNumber, message }
    })
  })
}

const getRemainingValidationVisibleMs = (startedAt: number): number => {
  return Math.max(0, VALIDATION_MIN_VISIBLE_MS - (Date.now() - startedAt))
}

export const useEcoModulationImportFlow = ({
  onClose,
  sorts,
  filters,
}: UseEcoModulationImportFlowParams) => {
  const { t } = useTranslation(["dataTransfer", "common"])
  const { user } = useAuth()
  const organizationId = user?.organization?.id ?? ""

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [response, setResponse] =
    useState<ValidateEcoModulationImportResponseDto | null>(null)
  const [confirmPhase, setConfirmPhase] = useState<ConfirmPhase>("idle")
  const [confirmJobId, setConfirmJobId] = useState<string | null>(null)
  const [isValidationVisible, setIsValidationVisible] = useState(false)
  const [isProgressActive, setIsProgressActive] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [progressRunId, setProgressRunId] = useState(0)
  const [failedIngestionId, setFailedIngestionId] = useState<string | null>(
    null
  )
  const [failedIngestionProductCount, setFailedIngestionProductCount] =
    useState<number | null>(null)

  const requestIdRef = useRef(0)
  const validationStartedAtRef = useRef<number | null>(null)

  const hasConfirmLaunched = confirmPhase !== "idle"
  const isConfirming = confirmPhase === "running"

  const confirmPollParams = useMemo(
    () => ({
      sorts,
      filters: {
        type: [findIngestionsFiltersDtoTypeEnum.ECO_MODULATION],
      },
    }),
    [sorts]
  )

  const resetState = useCallback((): void => {
    requestIdRef.current += 1
    setSelectedFile(null)
    setResponse(null)
    setConfirmPhase("idle")
    setConfirmJobId(null)
    setIsValidationVisible(false)
    setIsProgressActive(false)
    setRequestError(null)
    setFailedIngestionId(null)
    setFailedIngestionProductCount(null)
  }, [])

  const { mutate: validateImport } = useValidateEcoModulationImport({
    mutation: {
      onError: (error: AxiosError) => {
        const requestId = requestIdRef.current
        const startedAt = validationStartedAtRef.current ?? Date.now()
        const remainingMs = getRemainingValidationVisibleMs(startedAt)
        window.setTimeout(() => {
          if (requestId !== requestIdRef.current) {
            return
          }
          setIsValidationVisible(false)
          setIsProgressActive(false)
          setRequestError(error.message)
        }, remainingMs)
      },
      onSuccess: (data) => {
        const requestId = requestIdRef.current
        const startedAt = validationStartedAtRef.current ?? Date.now()
        const remainingMs = getRemainingValidationVisibleMs(startedAt)
        window.setTimeout(() => {
          if (requestId !== requestIdRef.current) {
            return
          }
          setResponse(data.data)
          setIsValidationVisible(false)
          setIsProgressActive(false)
        }, remainingMs)
      },
    },
  })

  const { mutate: confirmImport, isPending: isConfirmRequestPending } =
    useConfirmEcoModulationImport({
      mutation: {
        onError: (error: AxiosError) => {
          setConfirmPhase("failed")
          setRequestError(error.message)
          showAxiosError({
            notificationTitle: t(
              "dataTransfer:import.ecoModulation.summaryModal.errorTitle"
            ),
            notificationMessage: t(
              "dataTransfer:import.ecoModulation.summaryModal.errorMessage"
            ),
            showNotification: true,
            error,
          })
        },
        onSuccess: (data) => {
          setConfirmPhase("running")
          setConfirmJobId(data.data.jobId)
          void queryClient.invalidateQueries({
            queryKey: findIngestionsQueryKey({ sorts, filters }),
          })
          void queryClient.invalidateQueries({
            queryKey: findIngestionsQueryKey(confirmPollParams),
          })
        },
      },
    })

  const isFileBusy = isValidationVisible || isConfirming

  const { data: ingestionsData } = useFindIngestions(confirmPollParams, {
    query: {
      enabled: isConfirming && confirmJobId !== null,
      refetchInterval:
        isConfirming && confirmJobId !== null ? INGESTION_POLL_MS : false,
    },
  })

  const { data: errorLogsResponse } = useFindIngestionErrorLogs(
    failedIngestionId ?? undefined,
    {
      query: {
        enabled: confirmPhase === "failed" && failedIngestionId !== null,
        retry: 3,
      },
    }
  )

  useEffect(() => {
    if (!isFileBusy) {
      return
    }
    setIsProgressActive(false)
    let innerFrameId = 0
    const outerFrameId = window.requestAnimationFrame(() => {
      innerFrameId = window.requestAnimationFrame(() => {
        setIsProgressActive(true)
      })
    })
    return () => {
      window.cancelAnimationFrame(outerFrameId)
      window.cancelAnimationFrame(innerFrameId)
    }
  }, [isFileBusy, progressRunId])

  useEffect(() => {
    if (!isConfirming || confirmJobId === null) {
      return
    }

    const items = ingestionsData?.data ?? []
    const ecoModIngestion = items.find((item) => {
      return item.jobIds.includes(confirmJobId)
    })

    if (ecoModIngestion === undefined) {
      return
    }

    if (
      ecoModIngestion.status === findIngestionsResponseDtoStatusEnum.PENDING
    ) {
      return
    }

    if (
      ecoModIngestion.status ===
      findIngestionsResponseDtoStatusEnum.ECO_MODULATION_INGESTED
    ) {
      showSuccessNotification({
        title: t("dataTransfer:import.ecoModulation.summaryModal.successTitle"),
        message: t(
          "dataTransfer:import.ecoModulation.summaryModal.successMessage"
        ),
      })
      resetState()
      onClose()
      return
    }

    showAxiosError({
      notificationTitle: t(
        "dataTransfer:import.ecoModulation.summaryModal.errorTitle"
      ),
      notificationMessage: t(
        "dataTransfer:import.ecoModulation.summaryModal.errorMessage"
      ),
      showNotification: true,
      error: new AxiosError("Eco-modulation import failed"),
    })
    setConfirmPhase("failed")
    setRequestError("Eco-modulation import failed")
    if (ecoModIngestion.status === findIngestionsResponseDtoStatusEnum.ERROR) {
      setFailedIngestionId(ecoModIngestion.id)
      setFailedIngestionProductCount(ecoModIngestion.product_count)
    }
  }, [confirmJobId, ingestionsData, isConfirming, onClose, resetState, t])

  const handleClose = (): void => {
    resetState()
    onClose()
  }

  const handleDrop = (files: File[]): void => {
    const file = files[0]
    if (file === undefined || organizationId === "") {
      return
    }

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    setSelectedFile(file)
    setResponse(null)
    setRequestError(null)
    setIsProgressActive(false)
    setProgressRunId(requestId)
    setIsValidationVisible(true)
    validationStartedAtRef.current = Date.now()

    void file.text().then((csvContent) => {
      if (requestId !== requestIdRef.current) {
        return
      }
      validateImport({
        data: {
          csvContent,
          organizationId,
        },
      })
    })
  }

  const handleReject = (fileRejections: FileRejection[]): void => {
    const rejectedFile = fileRejections[0]?.file
    if (rejectedFile === undefined) {
      return
    }
    requestIdRef.current += 1
    setSelectedFile(rejectedFile)
    setResponse(null)
    setRequestError(FILE_FORMAT_REJECT_ERROR)
    setIsValidationVisible(false)
    setIsProgressActive(false)
  }

  const handleRemoveFile = (): void => {
    requestIdRef.current += 1
    setSelectedFile(null)
    setResponse(null)
    setIsValidationVisible(false)
    setIsProgressActive(false)
    setConfirmPhase("idle")
    setConfirmJobId(null)
    setRequestError(null)
    setFailedIngestionId(null)
    setFailedIngestionProductCount(null)
  }

  const handleConfirm = (): void => {
    if (organizationId === "") {
      return
    }
    const runId = requestIdRef.current + 1
    requestIdRef.current = runId
    setProgressRunId(runId)
    confirmImport({
      data: { organizationId },
    })
  }

  const validationErrors = response?.details.validationErrors ?? []
  const processRowErrors = mapIngestionErrorLogsToRowErrors(
    errorLogsResponse?.data ?? null
  )
  const hasRequestError =
    !isFileBusy && selectedFile !== null && requestError !== null
  const isFormatRejectError = requestError === FILE_FORMAT_REJECT_ERROR
  const hasFileError = hasRequestError
  const fileErrorDetail =
    hasRequestError && !isFormatRejectError
      ? t("dataTransfer:import.ecoModulation.summaryModal.errorMessage")
      : t("dataTransfer:import.ecoModulation.summaryModal.fileErrorDetail", {
          fileName: selectedFile?.name ?? "",
        })
  const isFileComplete = !isFileBusy && selectedFile !== null && !hasFileError
  const shouldShowValidationSummary =
    !hasConfirmLaunched &&
    response !== null &&
    !isValidationVisible &&
    requestError === null
  const shouldShowFollowInTableHint =
    isConfirming || (confirmPhase === "failed" && confirmJobId !== null)

  const canConfirm =
    !hasConfirmLaunched &&
    response !== null &&
    response.details.validationErrors.length === 0 &&
    response.summary.validRows > 0

  return {
    selectedFile,
    response,
    hasConfirmLaunched,
    isConfirming,
    isConfirmRequestPending,
    isFileBusy,
    isProgressActive,
    progressRunId,
    failedIngestionProductCount,
    validationErrors,
    processRowErrors,
    hasRequestError,
    isFormatRejectError,
    hasFileError,
    fileErrorDetail,
    isFileComplete,
    shouldShowValidationSummary,
    shouldShowFollowInTableHint,
    canConfirm,
    handleClose,
    handleDrop,
    handleReject,
    handleRemoveFile,
    handleConfirm,
  }
}
